/**
 * src/pages/Dashboard.jsx
 * Estado: el parte a la izquierda y la lectura del día en tarjetas a la
 * derecha. Una sola pantalla, sin scroll largo.
 *
 * Rediseño del 27-08 (hilo de distribución de la información), decidido por
 * Manuel módulo a módulo:
 *   · fuera la tarjeta «Clientes» (duplicaba la sección Clientes)
 *   · fuera la tarjeta «Actividad»: su gráfico vive ahora en Ejecuciones
 *   · fuera «Cifras de la landing»: es página propia del menú (Negocio)
 *   · fuera el botón «Nuevo cliente»: ya está en Clientes, que es su sitio
 *   · «Actualizar» re-mide las alertas re-medibles y cierra las resueltas
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Button } from '../components/ui/Button'
import { Parte } from '../components/dashboard/Parte'
import { TablaFrescura } from '../components/dashboard/TablaFrescura'
import { ColumnaCifras } from '../components/dashboard/ColumnaCifras'
import { useEstado, useFrescura, useRechequearEstado } from '../hooks/useAdminPanel'
import { fechaCL } from '../lib/horaChile'

export default function Dashboard() {
  const { data: estado, isLoading: cargandoEstado } = useEstado()
  // Query propia: si la frescura se cae, el parte se sigue viendo.
  const { data: frescura, isLoading: cargandoFrescura,
          error: errorFrescura } = useFrescura()
  const rechequear = useRechequearEstado()
  const [parte, setParte] = useState(null)   // resultado breve del re-chequeo

  async function actualizar() {
    setParte(null)
    try {
      const r = await rechequear.mutateAsync()
      const n = r?.resueltas?.length ?? 0
      const pendientesManual = r?.no_medibles?.length ?? 0
      setParte(
        n > 0
          ? `${n} ${n === 1 ? 'alerta resuelta' : 'alertas resueltas'}`
          : pendientesManual > 0
            ? 'nada que cerrar en lo re-medible'
            : 'todo re-medido: sin cambios'
      )
    } catch {
      setParte('no se pudo re-chequear')
    }
    setTimeout(() => setParte(null), 6000)
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Estado"
        subtitle={fechaCL(new Date(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            {parte && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                color: 'var(--color-text-3)',
              }}>
                {parte}
              </span>
            )}
            <Button
              size="sm" variant="secondary"
              loading={rechequear.isPending}
              icon={!rechequear.isPending && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              )}
              onClick={actualizar}
            >
              Actualizar
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 'var(--space-7)',
          alignItems: 'start',
        }}>
          {/* Izquierda: el parte — banda y lista de atenciones. */}
          <Parte datos={estado} cargando={cargandoEstado} />

          {/* Derecha: la lectura del día en cuadros, a la altura de los
              titulares (Manuel, 27-08). */}
          <ColumnaCifras cifras={estado?.cifras ?? []} cargando={cargandoEstado} />
        </div>

        {/* Debajo y a todo lo ancho (Manuel, 08-09): son cinco columnas y en los
            320 px de la derecha no caben sin apretarlas. Va después del parte
            porque contesta a la pregunta siguiente: primero «¿hay algo roto?»,
            y luego «¿a quién le falta actualizar algo?». */}
        <div style={{ marginTop: 'var(--space-7)' }}>
          <TablaFrescura datos={frescura} cargando={cargandoFrescura}
                         error={errorFrescura} />
        </div>
      </div>
    </div>
  )
}
